import React from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Info,
  Loader2,
  MessageCircle,
  QrCode,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  Unplug,
  Wifi
} from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import PageHeader from '../../components/common/PageHeader';
import { ADMIN_PERMISSIONS } from '../../constants';
import { adminAPI } from '../../services/api';
import useAuthStore from '../../store/authStore';
import { hasPermission } from '../../utils/rbac';

const extractError = (error, fallback) => error?.response?.data?.error || error?.response?.error || error?.message || fallback;

const labelize = (value = '') => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const shouldPoll = (whatsapp) => {
  if (!whatsapp || !whatsapp.enabled) return false;
  return !whatsapp.connected || ['initializing', 'connecting', 'qr'].includes(whatsapp.status);
};

const connectionCopy = (whatsapp) => {
  if (!whatsapp) {
    return {
      label: 'Loading',
      title: 'Checking sender status',
      detail: 'Loading the current WhatsApp sender state.',
      tone: 'slate'
    };
  }

  if (!whatsapp.enabled) {
    return {
      label: 'Disabled',
      title: 'Sender service disabled',
      detail: 'WhatsApp sending is disabled on the API service.',
      tone: 'amber'
    };
  }

  if (whatsapp.connected) {
    return {
      label: 'Connected',
      title: 'Sender is live',
      detail: `${whatsapp.displayPhone || whatsapp.phoneJid || 'The connected number'} is ready for OTPs and candidate WhatsApp messages.`,
      tone: 'emerald'
    };
  }

  if (whatsapp.status === 'logged_out') {
    return {
      label: 'Session ended',
      title: 'Pair the sender again',
      detail: 'The stored WhatsApp session is no longer usable. Generate a fresh QR code and scan it from the sender phone.',
      tone: 'amber'
    };
  }

  if (whatsapp.qr?.dataUrl || whatsapp.status === 'qr') {
    return {
      label: 'Pairing',
      title: 'QR ready to scan',
      detail: 'Scan this code from WhatsApp Linked Devices. Status refreshes automatically while pairing is in progress.',
      tone: 'sky'
    };
  }

  if (whatsapp.status === 'error') {
    return {
      label: 'Needs attention',
      title: 'Sender needs attention',
      detail: whatsapp.lastError || 'Start a new pairing session or check the API logs.',
      tone: 'rose'
    };
  }

  return {
    label: labelize(whatsapp.status || 'Offline'),
    title: 'No sender connected',
    detail: 'Generate a QR code to pair the number used for outgoing WhatsApp messages.',
    tone: 'slate'
  };
};

const toneClasses = {
  slate: {
    badge: 'border-slate-200 bg-slate-100 text-slate-700',
    dot: 'bg-slate-400',
    panel: 'border-slate-200 bg-white',
    icon: 'border-slate-200 bg-slate-50 text-slate-700',
    accent: 'text-slate-600'
  },
  sky: {
    badge: 'border-sky-200 bg-sky-50 text-sky-700',
    dot: 'bg-sky-500',
    panel: 'border-sky-200 bg-sky-50',
    icon: 'border-sky-200 bg-white text-sky-700',
    accent: 'text-sky-700'
  },
  emerald: {
    badge: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    dot: 'bg-emerald-500',
    panel: 'border-emerald-200 bg-emerald-50',
    icon: 'border-emerald-200 bg-white text-emerald-700',
    accent: 'text-emerald-700'
  },
  amber: {
    badge: 'border-amber-200 bg-amber-50 text-amber-700',
    dot: 'bg-amber-500',
    panel: 'border-amber-200 bg-amber-50',
    icon: 'border-amber-200 bg-white text-amber-700',
    accent: 'text-amber-700'
  },
  rose: {
    badge: 'border-rose-200 bg-rose-50 text-rose-700',
    dot: 'bg-rose-500',
    panel: 'border-rose-200 bg-rose-50',
    icon: 'border-rose-200 bg-white text-rose-700',
    accent: 'text-rose-700'
  }
};

const StatusBadge = ({ copy }) => {
  const tone = toneClasses[copy.tone] || toneClasses.slate;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${tone.badge}`}>
      <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
      {copy.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, detail, tone = 'slate' }) => {
  const styles = toneClasses[tone] || toneClasses.slate;
  return (
    <article className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase text-slate-500">{label}</p>
          <p className="mt-2 truncate text-lg font-semibold tracking-tight text-slate-950">{value}</p>
        </div>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${styles.icon}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      {detail && <p className="mt-3 line-clamp-2 text-xs leading-5 text-slate-500">{detail}</p>}
    </article>
  );
};

const DetailRow = ({ label, value, muted }) => (
  <div className="grid gap-1 border-b border-slate-100 py-3 last:border-0 sm:grid-cols-[9rem_minmax(0,1fr)]">
    <span className="text-sm text-slate-500">{label}</span>
    <span className={`min-w-0 break-words text-sm font-semibold sm:text-right ${muted ? 'text-slate-500' : 'text-slate-900'}`}>
      {value}
    </span>
  </div>
);

const PairingStep = ({ number, title, detail }) => (
  <div className="flex gap-3 rounded-lg border border-slate-200 bg-white p-4">
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-950 text-xs font-semibold text-white">
      {number}
    </span>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      <p className="mt-1 text-xs leading-5 text-slate-500">{detail}</p>
    </div>
  </div>
);

const PageSkeleton = () => (
  <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8" aria-label="Loading WhatsApp sender">
    <div className="mb-6 space-y-3">
      <SkeletonBlock className="h-7 w-44 rounded-full" />
      <SkeletonBlock className="h-10 w-80 rounded-lg" />
      <SkeletonBlock className="h-5 w-full max-w-2xl rounded-lg" />
    </div>
    <SkeletonBlock className="mb-5 h-52 rounded-lg" />
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {[0, 1, 2, 3].map((item) => <SkeletonBlock key={item} className="h-28 rounded-lg" />)}
    </div>
    <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
      <SkeletonBlock className="h-[30rem] rounded-lg" />
      <SkeletonBlock className="h-[30rem] rounded-lg" />
    </div>
  </div>
);

function WhatsappSenderPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const canManage = hasPermission(user, ADMIN_PERMISSIONS.NOTIFICATIONS_MANAGE);

  const statusQuery = useQuery(
    ['admin-whatsapp-sender'],
    adminAPI.getWhatsappSenderStatus,
    {
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchInterval: (data) => (shouldPoll(data?.whatsapp) ? 3000 : false)
    }
  );

  const connectMutation = useMutation(adminAPI.connectWhatsappSender, {
    onSuccess: (data) => {
      queryClient.setQueryData(['admin-whatsapp-sender'], data);
      toast.success(data?.whatsapp?.connected ? 'WhatsApp sender is connected.' : 'QR pairing started.');
      queryClient.invalidateQueries(['admin-whatsapp-sender']);
    },
    onError: (error) => toast.error(extractError(error, 'WhatsApp pairing could not be started.'))
  });

  const disconnectMutation = useMutation(adminAPI.disconnectWhatsappSender, {
    onSuccess: (data) => {
      queryClient.setQueryData(['admin-whatsapp-sender'], data);
      toast.success('WhatsApp sender disconnected.');
      queryClient.invalidateQueries(['admin-whatsapp-sender']);
    },
    onError: (error) => toast.error(extractError(error, 'WhatsApp sender could not be disconnected.'))
  });

  const whatsapp = statusQuery.data?.whatsapp;
  const copy = connectionCopy(whatsapp);
  const tone = toneClasses[copy.tone] || toneClasses.slate;
  const isBusy = connectMutation.isLoading || disconnectMutation.isLoading || statusQuery.isFetching;
  const canRequestPairing = canManage && whatsapp?.enabled && whatsapp?.canConnect !== false && !whatsapp?.connected && !connectMutation.isLoading;
  const serviceState = whatsapp ? (whatsapp.enabled ? 'Sender service enabled' : 'Disabled in environment') : 'Status not loaded';
  const visibleLastError = Boolean(whatsapp?.lastError && !['logged_out', 'disconnected'].includes(whatsapp.status));
  const lastIssue = whatsapp?.lastError
    ? (whatsapp.status === 'logged_out' ? `${whatsapp.lastError} from previous session` : whatsapp.lastError)
    : 'None recorded';

  const handleDisconnect = () => {
    if (!whatsapp?.canDisconnect) return;
    const phone = whatsapp?.displayPhone || whatsapp?.phoneJid || 'the current sender';
    if (!window.confirm(`Disconnect ${phone}? WhatsApp messages will stop until a number is connected again.`)) return;
    disconnectMutation.mutate();
  };

  if (statusQuery.isLoading && !statusQuery.data) return <PageSkeleton />;

  return (
    <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <PageHeader
        title="WhatsApp Sender"
        description="Manage the sender number used for OTPs and candidate WhatsApp messages."
        actions={(
          <button
            type="button"
            onClick={() => connectMutation.mutate()}
            disabled={!canRequestPairing}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {connectMutation.isLoading ? <Loader2 size={16} className="animate-spin" /> : <QrCode size={16} />}
            {whatsapp?.hasQr ? 'Refresh QR' : 'Pair number'}
          </button>
        )}
      />

      {statusQuery.isError && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <span>{extractError(statusQuery.error, 'Unable to load WhatsApp sender status.')}</span>
        </div>
      )}

      {!canManage && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
          <Info size={18} className="mt-0.5 shrink-0" />
          <span>You have view-only access. Connecting or disconnecting the sender requires communications management permission.</span>
        </div>
      )}

      <section className={`mb-5 rounded-lg border ${tone.panel} shadow-sm`}>
        <div className="grid gap-0 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="min-w-0 p-5 sm:p-6">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <StatusBadge copy={copy} />
                <h2 className="mt-4 max-w-3xl text-2xl font-semibold tracking-tight text-slate-950">
                  {copy.title}
                </h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                  {copy.detail}
                </p>
              </div>
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border ${tone.icon}`}>
                {whatsapp?.connected ? <CheckCircle2 size={24} /> : <MessageCircle size={24} />}
              </span>
            </div>
          </div>

          <div className="border-t border-white/70 bg-white/70 p-5 xl:border-l xl:border-t-0">
            <div className="grid gap-3">
              <div>
                <p className="text-xs font-medium uppercase text-slate-500">Live state</p>
                <p className={`mt-1 text-base font-semibold ${tone.accent}`}>
                  {labelize(whatsapp?.status || 'not connected')}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-slate-500">Last connected</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{formatDateTime(whatsapp?.lastConnectedAt)}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Wifi}
          label="Connection"
          value={whatsapp?.connected ? 'Live' : labelize(whatsapp?.status || 'Offline')}
          detail={serviceState}
          tone={copy.tone}
        />
        <StatCard
          icon={Smartphone}
          label="Sender number"
          value={whatsapp?.displayPhone || 'None'}
          detail={whatsapp?.pushName || 'Disconnect first to pair another number'}
          tone="sky"
        />
        <StatCard
          icon={ShieldCheck}
          label="Device profile"
          value={`${whatsapp?.device?.os || 'iOS'} / ${whatsapp?.device?.browser || 'Safari'}`}
          detail={`Version ${whatsapp?.device?.version || '17.0'}`}
          tone="slate"
        />
        <StatCard
          icon={Clock3}
          label="Last issue"
          value={whatsapp?.lastError ? labelize(whatsapp.status || 'issue') : 'Clear'}
          detail={lastIssue}
          tone={visibleLastError ? 'rose' : 'amber'}
        />
      </section>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_24rem]">
        <section className="min-w-0 rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-slate-900">Pairing workspace</h2>
              <p className="mt-1 text-sm leading-5 text-slate-500">Generate a QR code, scan it, then keep the sender phone online.</p>
            </div>
            <StatusBadge copy={copy} />
          </div>

          <div className="grid gap-6 p-5 lg:grid-cols-[minmax(18rem,24rem)_minmax(0,1fr)] lg:p-6">
            <div className="min-w-0">
              <div className="flex min-h-[22rem] items-center justify-center rounded-lg border border-slate-200 bg-slate-50 p-4">
                {whatsapp?.connected ? (
                  <div className="text-center">
                    <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-600">
                      <CheckCircle2 size={42} />
                    </span>
                    <p className="mt-5 text-lg font-semibold text-slate-900">Connected</p>
                    <p className="mx-auto mt-2 max-w-[18rem] text-sm leading-6 text-slate-500">
                      {whatsapp.displayPhone || whatsapp.phoneJid || 'The current sender'} is active.
                    </p>
                  </div>
                ) : whatsapp?.qr?.dataUrl ? (
                  <div className="w-full text-center">
                    <img
                      src={whatsapp.qr.dataUrl}
                      alt="WhatsApp sender QR code"
                      className="mx-auto aspect-square w-full max-w-[20rem] rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
                    />
                    <p className="mx-auto mt-3 max-w-xs text-xs leading-5 text-slate-500">
                      Expires {formatDateTime(whatsapp.qr.expiresAt)}
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-dashed border-slate-300 bg-white text-slate-400">
                      <QrCode size={42} />
                    </span>
                    <p className="mt-5 text-lg font-semibold text-slate-900">No QR available</p>
                    <p className="mx-auto mt-2 max-w-[18rem] text-sm leading-6 text-slate-500">Generate a QR code to connect the sender number.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="min-w-0 space-y-5">
              {visibleLastError && (
                <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm leading-6 text-rose-700">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>{whatsapp.lastError}</p>
                </div>
              )}

              {whatsapp?.status === 'logged_out' && (
                <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-800">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>The previous WhatsApp session ended. Generate a new QR code to clear the stale session and pair the sender again.</p>
                </div>
              )}

              <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-1 2xl:grid-cols-3">
                <PairingStep number="1" title="Open Linked Devices" detail="Use the sender phone that will own outgoing messages." />
                <PairingStep number="2" title="Scan QR" detail="Scan the code before it expires and wait for live status." />
                <PairingStep number="3" title="Keep Online" detail="Keep the sender phone connected after pairing." />
              </div>

            </div>
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-100 px-5 pb-5 sm:flex-row sm:items-center sm:justify-end lg:px-6 lg:pb-6">
            <button
              type="button"
              onClick={() => connectMutation.mutate()}
              disabled={!canRequestPairing}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              {connectMutation.isLoading ? <Loader2 size={17} className="animate-spin" /> : <QrCode size={17} />}
              {whatsapp?.hasQr ? 'Refresh QR' : 'Generate QR'}
            </button>
            <button
              type="button"
              onClick={() => statusQuery.refetch()}
              disabled={isBusy}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              <RefreshCw size={17} className={statusQuery.isFetching ? 'animate-spin' : ''} />
              Refresh status
            </button>
          </div>
        </section>

        <aside className="space-y-5">
          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Session details</h2>
                <p className="mt-1 text-sm leading-5 text-slate-500">Current sender configuration.</p>
              </div>
              <ShieldCheck className="h-5 w-5 text-slate-400" />
            </div>
            <div className="mt-4">
              <DetailRow label="Provider" value={labelize(whatsapp?.provider || 'baileys')} />
              <DetailRow label="Session" value={whatsapp?.sessionId || 'Not available'} />
              <DetailRow label="Phone JID" value={whatsapp?.phoneJid || 'Not connected'} muted={!whatsapp?.phoneJid} />
              <DetailRow label="Push name" value={whatsapp?.pushName || 'Not available'} muted={!whatsapp?.pushName} />
              <DetailRow label="QR expires" value={formatDateTime(whatsapp?.qr?.expiresAt)} muted={!whatsapp?.qr?.expiresAt} />
              <DetailRow label="Last issue" value={lastIssue} muted={!whatsapp?.lastError} />
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Controls</h2>
                <p className="mt-1 text-sm leading-5 text-slate-500">Disconnect only when replacing the sender.</p>
              </div>
              <Unplug className="h-5 w-5 text-slate-400" />
            </div>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={!canManage || disconnectMutation.isLoading || !whatsapp?.canDisconnect}
              className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-4 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {disconnectMutation.isLoading ? <Loader2 size={17} className="animate-spin" /> : <Unplug size={17} />}
              Disconnect sender
            </button>
            <p className="mt-3 text-xs leading-5 text-slate-500">
              WhatsApp jobs may remain queued, but delivery needs a connected sender.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}

export default WhatsappSenderPage;
